import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";
// import { uploadImageToCloudinary } from "../helpers/cloudinary";

export interface Vendor {
  id: number;
  location_id: number;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  location?: {
    id: number;
    name: string;
    address: string;
    code: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
}

export const useVendors = (page: number, filters: Record<string, any> = {}) => {
  return useQuery({
    queryKey: ["vendors", page, filters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== null && v !== "")
        ),
      }).toString();

      const response = await apiClient.get<{
        data: Vendor[];
        meta: { total: number; current_page: number; last_page: number };
        links: {
          first: string;
          last: string;
          prev: string | null;
          next: string | null;
        };
      }>(`/vendor?${queryParams}`);

      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorData: {
      location_id: number;
      name: string;
      email: string;
      phone: string;
      avatar: string;
    }) => {
      const response = await apiClient.post("/vendor/create", vendorData);
      return response.data;
    },
    onSuccess: () => {
      message.success("Vendor created successfully!");
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: () => {
      message.error("Failed to create vendor. Please try again.");
    },
  });
};

export const useUpdateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorData: {
      id: number;
      location_id: number;
      name: string;
      email: string;
      phone: string;
      avatar: string;
    }) => {
      const response = await apiClient.put(
        `/vendor/${vendorData.id}`,
        vendorData
      );
      return response.data;
    },
    onSuccess: () => {
      message.success("Vendor updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: () => {
      message.error("Failed to update vendor. Please try again.");
    },
  });
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorId: number) => {
      const response = await apiClient.delete(`/vendor/${vendorId}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("Vendor deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
    onError: () => {
      message.error("Failed to delete vendor. Please try again.");
    },
  });
};
